// CMI.5 — public co-manager accept page. A colleague opens /join/{token},
// sees the inviting company, sets a name + password, and accepts. Mirrors
// SignUp's two-column editorial layout. Reads the anonymous preview
// (GET /invite/company/{token}) for the header; POSTs /auth/register/comanager.
// The response is a generic 202 (anti-enumeration), so the done panel is
// identical whether or not the email already existed.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, type FormEvent } from 'react';

import { EntityDisclaimer } from '../../components/EntityDisclaimer';

import { Honeypot, TurnstileWidget } from '../../lib/turnstile';
import { useNavigate, useParams } from 'react-router-dom';

import { Brand, Kicker } from '../../components/shell';
import { api } from '../../lib/api';

interface InvitePreview {
  company_name: string | null;
  is_usable: boolean;
}

export default function JoinCompany() {
  const navigate = useNavigate();
  const { token = '' } = useParams();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api<InvitePreview>(`/invite/company/${token}`)
      .then(d => { if (!cancelled) setPreview(d); })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, [token]);

  const [captcha, setCaptcha] = useState('');
  const [website, setWebsite] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api('/auth/register/comanager', {
        method: 'POST',
        body: {
          token, name: name.trim(), password, website,
          ...(captcha ? { cf_turnstile_response: captcha } : {}),
        },
      });
      setDone(true);
    } catch (err: any) {
      const detail = err?.payload?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not accept this invite. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // A dead invite (revoked/expired/accepted/unknown) reads as not usable, or the
  // preview call failed outright.
  const dead = loadFailed || (preview !== null && !preview.is_usable);
  const companyName = preview?.company_name;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--paper)' }}>
      <header style={{ padding: '24px var(--pad-x)', borderBottom: 'var(--hairline)' }}>
        <div style={{ maxWidth: 'var(--max)', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Brand onClick={() => navigate('/')} />
        </div>
      </header>

      <main
        style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'stretch', minHeight: 0 }}
        className="signin-grid"
      >
        {/* Left — editorial column */}
        <section style={{
          padding: '80px var(--pad-x)',
          borderRight: 'var(--hairline)',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        }}>
          <div>
            <Kicker>Co-manager invite</Kicker>
            <h1 className="page-title" style={{ maxWidth: '16ch', margin: '0 0 32px' }}>
              {companyName ? <>Join <em>{companyName}</em>.</> : <>A <em>co-manager</em> invite.</>}
            </h1>
            <p className="serif-body" style={{ maxWidth: '42ch', margin: 0 }}>
              Accepting adds you as a manager of this company — you will be able to
              add investors, add funds, and enter transactions, within this company only.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 64 }}>
            <hr className="rule" />
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 24 }}>
              <span className="label">SECURED</span>
              <span className="serif-body" style={{ fontSize: 16, color: 'var(--ink-2)' }}>
                Email verification, then two-factor authentication on every sign-in.
              </span>
            </div>
            <hr className="rule" />
          </div>
        </section>

        {/* Right — form / done / dead column */}
        <section style={{
          padding: '80px var(--pad-x)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ width: '100%', maxWidth: 420 }}>
            {preview === null && !loadFailed ? (
              <span className="label-s" style={{ color: 'var(--ink-3)' }}>LOADING…</span>
            ) : dead ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                <Kicker>Invite unavailable</Kicker>
                <h2 className="page-title" style={{ margin: 0, fontSize: 32 }}><em>This invite is no longer active.</em></h2>
                <p className="serif-body" style={{ margin: 0, fontSize: 16, color: 'var(--ink-2)' }}>
                  The link may have expired, been revoked, or already been used. Ask the
                  manager who invited you to send a fresh invite.
                </p>
                <div>
                  <button className="btn" onClick={() => navigate('/sign-in')}>
                    To sign in <span className="btn-arrow">→</span>
                  </button>
                </div>
              </div>
            ) : done ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                <Kicker>Check your email</Kicker>
                <h2 className="page-title" style={{ margin: 0, fontSize: 32 }}><em>Almost there.</em></h2>
                <p className="serif-body" style={{ margin: 0, fontSize: 16, color: 'var(--ink-2)' }}>
                  We have sent a verification link to confirm your account. Click it, then
                  sign in and set up two-factor authentication to finish.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                  <button className="btn" onClick={() => navigate('/sign-in')}>
                    To sign in <span className="btn-arrow">→</span>
                  </button>
                  <button className="btn-link" onClick={() => navigate('/verify-email')}>
                    Resend link
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                <Kicker>Accept your invite</Kicker>

                <div className="field">
                  <label className="field-label">Your name</label>
                  <input className="field-input" type="text" value={name}
                         onChange={e => setName(e.target.value)} autoComplete="name" required autoFocus />
                </div>

                <div className="field">
                  <label className="field-label">Password</label>
                  <input className="field-input" type="password" value={password}
                         onChange={e => setPassword(e.target.value)} autoComplete="new-password"
                         minLength={14} required />
                  {/* OB.4 — state the rule up front; matches the server's
                      MinimumLengthValidator (min_length=14). */}
                  <p className="field-help" style={{ marginTop: 8 }}>
                    At least 14 characters. Avoid common or all-number passwords.
                  </p>
                </div>

                {error && (
                  <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15, color: 'var(--accent)' }}>
                    {error}
                  </div>
                )}

                <Honeypot value={website} onChange={setWebsite} />
                <TurnstileWidget onToken={setCaptcha} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 8 }}>
                  <button type="submit" className="btn" disabled={submitting}>
                    {submitting ? 'Accepting…' : <>Accept invite <span className="btn-arrow">→</span></>}
                  </button>
                  <button type="button" className="btn-link" onClick={() => navigate('/sign-in')}>
                    Have an account? Sign in
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>

      <footer style={{
        padding: '20px var(--pad-x)', borderTop: 'var(--hairline)',
        fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.16em',
        textTransform: 'uppercase', color: 'var(--ink-3)',
        display: 'flex', justifyContent: 'space-between',
      }}>
        {/* adviser-status label removed — LC-REV9-001 sweep via BH-RAPP-001 */}
        <span>Rev9 Capital · Secured session</span>
        <span>Miami, FL</span>
      </footer>
      <div style={{ padding: '0 var(--pad-x) 20px' }}>
        <EntityDisclaimer style={{ margin: 0 }} />
      </div>

      <style>{`
        @media (max-width: 900px) {
          .signin-grid { grid-template-columns: 1fr !important; }
          .signin-grid > section:first-child { border-right: 0; border-bottom: var(--hairline); }
        }
      `}</style>
    </div>
  );
}
